import React from 'react';
import { Settings, Users, MapPin, Activity, Server, Shield, ShieldCheck, AlertCircle, Link2, UserCheck, FileText, Wand2, Database, Clock } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UsersTab } from '@/components/admin/UsersTab';
import { CentrosTab } from '@/components/admin/CentrosTab';
import { AuditTab } from '@/components/admin/AuditTab';
import { SystemTab } from '@/components/admin/SystemTab';
import { SolicitudesTab } from '@/components/admin/SolicitudesTab';
import { SemaforoConfigTab } from '@/components/admin/SemaforoConfigTab';
import { RLSTab } from '@/components/admin/RLSTab';
import { SinSedeTab } from '@/components/admin/SinSedeTab';
import { RespOverridesTab } from '@/components/admin/RespOverridesTab';
import { MapeoSedesTab } from '@/components/admin/MapeoSedesTab';
import { SedeCodeCorrectionsTab } from '@/components/admin/SedeCodeCorrectionsTab';
import { MapeoPersonasTab } from '@/components/admin/MapeoPersonasTab';
import { CalidadTab } from '@/components/admin/CalidadTab';
import { SedeOverridesTab } from '@/components/admin/SedeOverridesTab';
import { LogaTab } from '@/components/admin/LogaTab';
import { ObjetivosTab } from '@/components/admin/ObjetivosTab';
import { AccesosTab } from '@/components/admin/AccesosTab';
import { CuarentenaTab } from '@/components/admin/CuarentenaTab';
import { useUserStore } from '@/store/userStore';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const { token } = useUserStore();

  const handleDownloadManual = async () => {
    try {
      const res = await fetch(`${API}/api/admin/manual-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al descargar');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Manual_Administracion_Valoriza.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('No se pudo descargar el manual. Inténtalo de nuevo.');
    }
  };

  return (
    <div data-testid="admin-page">
      <div className="px-4 lg:px-6 py-6">
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-montserrat font-bold text-xl text-white">Panel de Administración</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--vz-text-secondary)' }}>Gestión de usuarios, centros, auditoría y estado del sistema</p>
          </div>
          <button
            onClick={handleDownloadManual}
            data-testid="download-manual-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80 flex-shrink-0"
            style={{ background: 'rgba(74,158,255,0.12)', color: '#4A9EFF', border: '1px solid rgba(74,158,255,0.3)' }}
            title="Descargar manual PDF de administración"
          >
            <FileText size={13} />
            Manual PDF
          </button>
        </div>

        <Tabs defaultValue="users">
          <div className="overflow-x-auto mb-6 -mx-1 px-1">
            <TabsList className="h-9 w-max" style={{ background: 'var(--vz-bg-elevated)' }}>
              <TabsTrigger value="users" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-users">
                <Users size={12} /> Usuarios
              </TabsTrigger>
              <TabsTrigger value="solicitudes" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-solicitudes">
                <ShieldCheck size={12} /> Solicitudes
              </TabsTrigger>
              <TabsTrigger value="accesos" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-accesos">
                <Clock size={12} /> Accesos
              </TabsTrigger>
              <TabsTrigger value="rls" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-rls">
                <Shield size={12} /> Accesos RLS
              </TabsTrigger>
              <TabsTrigger value="centros" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-centros">
                <MapPin size={12} /> Centros
              </TabsTrigger>
              <TabsTrigger value="sin-sede" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-sin-sede">
                <AlertCircle size={12} /> Sin Sede
              </TabsTrigger>
              <TabsTrigger value="resp-overrides" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-resp-overrides">
                <UserCheck size={12} /> Overrides Resp.
              </TabsTrigger>
              <TabsTrigger value="mapeo-sedes" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-mapeo-sedes">
                <Link2 size={12} /> Mapeo Sedes
              </TabsTrigger>
              <TabsTrigger value="code-corrections" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-code-corrections">
                <Link2 size={12} /> Código→Código
              </TabsTrigger>
              <TabsTrigger value="mapeo-personas" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-mapeo-personas">
                <UserCheck size={12} /> Mapeo Personas
              </TabsTrigger>
              <TabsTrigger value="sede-overrides" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-sede-overrides">
                <Database size={12} /> Mapeo Texto
              </TabsTrigger>
              <TabsTrigger value="loga" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-loga">
                <Database size={12} /> Mapeo LOGA
              </TabsTrigger>
              <TabsTrigger value="objetivos" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-objetivos">
                <Database size={12} /> Objetivos
              </TabsTrigger>
              <TabsTrigger value="calidad" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-calidad">
                <Wand2 size={12} /> Calidad & Notif.
              </TabsTrigger>
              <TabsTrigger value="cuarentena" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-cuarentena">
                <AlertCircle size={12} /> Cuarentena
              </TabsTrigger>
              <TabsTrigger value="audit" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-audit">
                <Activity size={12} /> Auditoría
              </TabsTrigger>
              <TabsTrigger value="system" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-system">
                <Server size={12} /> Sistema
              </TabsTrigger>
              <TabsTrigger value="semaforo-config" className="text-xs gap-1.5 flex-shrink-0" data-testid="admin-tab-semaforo-config">
                🚦 Semáforo
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="rounded-2xl border p-5" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
            <TabsContent value="users"><UsersTab /></TabsContent>
            <TabsContent value="solicitudes"><SolicitudesTab /></TabsContent>
            <TabsContent value="accesos"><AccesosTab /></TabsContent>
            <TabsContent value="rls"><RLSTab /></TabsContent>
            <TabsContent value="centros"><CentrosTab /></TabsContent>
            <TabsContent value="sin-sede"><SinSedeTab /></TabsContent>
            <TabsContent value="resp-overrides"><RespOverridesTab /></TabsContent>
            <TabsContent value="mapeo-sedes"><MapeoSedesTab /></TabsContent>
            <TabsContent value="code-corrections"><SedeCodeCorrectionsTab /></TabsContent>
            <TabsContent value="mapeo-personas"><MapeoPersonasTab /></TabsContent>
            <TabsContent value="sede-overrides"><SedeOverridesTab /></TabsContent>
            <TabsContent value="loga"><LogaTab /></TabsContent>
            <TabsContent value="objetivos"><ObjetivosTab /></TabsContent>
            <TabsContent value="calidad"><CalidadTab /></TabsContent>
            <TabsContent value="cuarentena"><CuarentenaTab /></TabsContent>
            <TabsContent value="audit"><AuditTab /></TabsContent>
            <TabsContent value="system"><SystemTab /></TabsContent>
            <TabsContent value="semaforo-config"><SemaforoConfigTab /></TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
